/**
 * CMS SDK - CategoryManager 组件
 * 分类管理
 */

import React, { useState, useCallback } from 'react';
import { t } from '../i18n';

/**
 * 分类管理组件
 * @param {Object} props - 组件属性
 */
export function CategoryManager({
  categories = [],
  loading = false,
  onCreate,
  onUpdate,
  onDelete,
  className = '',
}) {
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', parent_id: '' });
  const [showForm, setShowForm] = useState(false);

  // 开始编辑
  const startEdit = useCallback((category) => {
    setEditingId(category.id);
    setFormData({
      name: category.name,
      description: category.description || '',
      parent_id: category.parent_id || '',
    });
    setShowForm(true);
  }, []);

  // 开始创建
  const startCreate = useCallback(() => {
    setEditingId(null);
    setFormData({ name: '', description: '', parent_id: '' });
    setShowForm(true);
  }, []);

  // 取消
  const handleCancel = useCallback(() => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ name: '', description: '', parent_id: '' });
  }, []);

  // 保存
  const handleSave = useCallback(async () => {
    if (!formData.name.trim()) return;
    
    try {
      if (editingId) {
        await onUpdate?.(editingId, formData);
      } else {
        await onCreate?.(formData);
      }
      handleCancel();
    } catch (err) {
      console.error('Save category error:', err);
    }
  }, [formData, editingId, onCreate, onUpdate, handleCancel]);

  // 删除
  const handleDelete = useCallback(async (categoryId) => {
    if (!confirm(t('confirmDelete'))) return;
    try {
      await onDelete?.(categoryId);
    } catch (err) {
      console.error('Delete category error:', err);
    }
  }, [onDelete]);

  // 递归渲染分类树
  const renderCategory = (category, depth = 0) => {
    return (
      <div 
        key={category.id}
        className="cms-category-item"
        style={{ paddingLeft: `${depth * 24}px` }}
      >
        <div className="cms-category-content">
          <span className="cms-category-icon">
            {category.children?.length > 0 ? '📁' : '📄'}
          </span>
          <span className="cms-category-name">{category.name}</span>
          {category.content_count > 0 && (
            <span className="cms-category-count">({category.content_count})</span>
          )}
        </div>
        <div className="cms-category-actions">
          <button 
            className="cms-action-btn"
            onClick={() => startEdit(category)}
          >
            ✏️
          </button>
          <button 
            className="cms-action-btn"
            onClick={() => handleDelete(category.id)}
          >
            🗑️
          </button>
        </div>
        {category.children?.map(child => renderCategory(child, depth + 1))}
      </div>
    );
  };

  // 构建分类树
  const buildTree = (items, parentId = null) => {
    return items
      .filter(item => item.parent_id === parentId)
      .sort((a, b) => a.sort_order - b.sort_order)
      .map(item => ({
        ...item,
        children: buildTree(items, item.id),
      }));
  };

  const categoryTree = buildTree(categories);

  return (
    <div className={`cms-category-manager ${className}`}>
      {/* 工具栏 */}
      <div className="cms-manager-toolbar">
        <h3>{t('manageCategories')}</h3>
        <button 
          className="cms-btn cms-btn-primary"
          onClick={startCreate}
        >
          {t('addCategory')}
        </button>
      </div>

      {/* 创建/编辑表单 */}
      {showForm && (
        <div className="cms-category-form">
          <div className="cms-form-group">
            <label>分类名称</label>
            <input
              type="text"
              className="cms-input"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="请输入分类名称"
            />
          </div>
          <div className="cms-form-group">
            <label>父级分类</label>
            <select
              className="cms-select"
              value={formData.parent_id}
              onChange={(e) => setFormData(prev => ({ ...prev, parent_id: e.target.value }))}
            >
              <option value="">无 (顶级分类)</option>
              {categories
                .filter(c => c.id !== editingId)
                .map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))
              }
            </select>
          </div>
          <div className="cms-form-group">
            <label>描述</label>
            <textarea
              className="cms-input"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="可选描述"
              rows={2}
            />
          </div>
          <div className="cms-form-actions">
            <button className="cms-btn cms-btn-secondary" onClick={handleCancel}>
              {t('cancel')}
            </button>
            <button className="cms-btn cms-btn-primary" onClick={handleSave}>
              {t('save')}
            </button>
          </div>
        </div>
      )}

      {/* 分类列表 */}
      {loading ? (
        <div className="cms-loading">
          <div className="cms-loading-spinner" />
          <span>{t('loading')}</span>
        </div>
      ) : categoryTree.length === 0 ? (
        <div className="cms-empty">
          <span className="cms-empty-icon">📂</span>
          <span>暂无分类</span>
        </div>
      ) : (
        <div className="cms-category-list">
          {categoryTree.map(category => renderCategory(category))}
        </div>
      )}
    </div>
  );
}

export default CategoryManager;
